// SPDX-FileCopyrightText: 2026 Ebiose AI
// SPDX-License-Identifier: LicenseRef-Ebiose-Proprietary
// All rights reserved. Confidential — unauthorized distribution prohibited.

import { useCallback } from "react"
import { useSearchParams } from "react-router-dom"
import type { SortingState, VisibilityState } from "@tanstack/react-table"
import {
  decodePage,
  decodeSort,
  decodeVisibility,
  encodePage,
  encodeSort,
  encodeVisibility,
} from "@/lib/table-url-state"
import { useUrlParamWriter } from "@/hooks/useUrlParamWriter"

export interface UseTableUrlStateOptions {
  columnIds: readonly string[]
  defaultHidden?: readonly string[]
  nonHideable?: readonly string[]
  sortParam?: string
  pageParam?: string
  visibilityParam?: string
}

export interface TableUrlState {
  sorting: SortingState
  onSortingChange: (next: SortingState) => void
  pageIndex: number
  onPageIndexChange: (next: number) => void
  columnVisibility: VisibilityState
  onColumnVisibilityChange: (next: VisibilityState) => void
}

/**
 * Binds the pure URL-state codec to the router's search params: reads
 * TanStack's controlled sort / page / visibility off the URL and writes each
 * change back, page 1 and the empty states omitted so the canonical link
 * stays clean (ADR-0006). A new ordering renumbers the pages, so a sort
 * change resets the page; hiding a column does not.
 */
export function useTableUrlState({
  columnIds,
  defaultHidden = [],
  nonHideable = [],
  sortParam = "sort",
  pageParam = "page",
  visibilityParam = "cols",
}: UseTableUrlStateOptions): TableUrlState {
  const [searchParams] = useSearchParams()
  const write = useUrlParamWriter()

  const sorting = decodeSort(searchParams.get(sortParam), columnIds)
  const pageIndex = decodePage(searchParams.get(pageParam))

  const rawVisibility = searchParams.get(visibilityParam)
  const hidden =
    rawVisibility !== null
      ? decodeVisibility(rawVisibility, columnIds)
      : Object.fromEntries(defaultHidden.map((id) => [id, false]))
  const nonHideableSet = new Set(nonHideable)
  const columnVisibility: VisibilityState = Object.fromEntries(
    columnIds.map((id) => [id, nonHideableSet.has(id) || hidden[id] !== false]),
  )

  const onSortingChange = useCallback(
    (next: SortingState) => {
      const encoded = encodeSort(next)
      write({ [sortParam]: encoded === "" ? null : encoded, [pageParam]: null })
    },
    [write, sortParam, pageParam],
  )

  const onPageIndexChange = useCallback(
    (next: number) => {
      write({ [pageParam]: next <= 0 ? null : encodePage(next) })
    },
    [write, pageParam],
  )

  const onColumnVisibilityChange = useCallback(
    (next: VisibilityState) => {
      write({ [visibilityParam]: encodeVisibility(next) })
    },
    [write, visibilityParam],
  )

  return {
    sorting,
    onSortingChange,
    pageIndex,
    onPageIndexChange,
    columnVisibility,
    onColumnVisibilityChange,
  }
}
